import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Flex, VStack, Heading, Text, Button, Spinner, Badge, Switch,
  Card, CardHeader, CardBody, FormControl, FormLabel, Input, useToast, useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchCollection, updateCollection, setCollectionPlaylists,
  collectionQueryKey, collectionsQueryKey,
  type PlaylistCollectionDetailDto,
} from '../../api/playlistCurationApi';
import { ApiError } from '../../services/apiClient';
import { SortablePlaylistList, type SortableRow } from './components/SortablePlaylistList';
import { PlaylistPickerModal } from './components/PlaylistPickerModal';

const cardShadow = '0px 18px 40px rgba(112, 144, 176, 0.12)';

/**
 * Non-default locales the admin can author titles for. Adding a language is a
 * one-line change here (the input list, the save payload, and validation all
 * derive from this constant).
 */
const TITLE_LOCALES = [
  { code: 'uk', labelKey: 'admin.playlistCollections.form.titleUk' },
] as const;

export function CollectionDetailPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const textColor = useColorModeValue('navy.700', 'white');

  const [title, setTitle] = useState('');
  // Per-locale titles keyed by locale code (e.g. { uk: '…' }).
  const [titles, setTitles] = useState<Record<string, string>>({});
  const [pickerOpen, setPickerOpen] = useState(false);

  const { data: collection, isLoading } = useQuery({
    queryKey: collectionQueryKey(slug!),
    queryFn: () => fetchCollection(slug!),
    enabled: Boolean(slug),
  });

  // Seed the title inputs once the collection loads (and when switching collections).
  useEffect(() => {
    if (collection) {
      setTitle(collection.title);
      setTitles({ ...(collection.titles ?? {}) });
    }
  }, [collection?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: collectionQueryKey(slug!) });
    queryClient.invalidateQueries({ queryKey: collectionsQueryKey });
  }, [queryClient, slug]);

  const saveDetailsMutation = useMutation({
    mutationFn: () => {
      // Drop blank locale values so we don't persist empty entries.
      const cleaned: Record<string, string> = {};
      for (const [code, value] of Object.entries(titles)) {
        const trimmed = value.trim();
        if (trimmed) cleaned[code] = trimmed;
      }
      return updateCollection(slug!, { title: title.trim(), titles: cleaned });
    },
    onSuccess: () => {
      invalidate();
      toast({ title: t('admin.playlistCollections.saved', 'Saved'), status: 'success', duration: 3000, isClosable: true });
    },
    onError: () => toast({ title: t('admin.playlistCollections.updateFailed', 'Failed to update collection'), status: 'error', duration: 4000, isClosable: true }),
  });

  const publishMutation = useMutation({
    mutationFn: (isPublished: boolean) => updateCollection(slug!, { isPublished }),
    onSuccess: () => invalidate(),
    onError: () => toast({ title: t('admin.playlistCollections.updateFailed', 'Failed to update collection'), status: 'error', duration: 4000, isClosable: true }),
  });

  // Full-replace of membership + order. Used for add, remove, and reorder.
  const setPlaylistsMutation = useMutation({
    mutationFn: (playlistIds: string[]) => setCollectionPlaylists(slug!, playlistIds),
    onSuccess: () => invalidate(),
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 422) {
        toast({ title: t('admin.playlistCollections.invalidMember', 'Only published library playlists can be added'), status: 'error', duration: 4000, isClosable: true });
      } else {
        toast({ title: t('admin.playlistCollections.membersFailed', 'Failed to update playlists'), status: 'error', duration: 4000, isClosable: true });
      }
      invalidate();
    },
  });

  const rows: SortableRow[] = useMemo(() =>
    (collection?.items ?? []).map((item) => ({
      id: item.playlist.id,
      title: item.playlist.name,
      subtitle: t('admin.playlistCollections.tracksSubtitle', { count: item.playlist.trackCount, defaultValue: '{{count}} tracks' }),
      thumbnailUrl: item.playlist.thumbnailUrl,
      isAvailable: item.isAvailable,
    })),
    [collection?.items, t],
  );

  const currentIds = useMemo(() => (collection?.items ?? []).map((i) => i.playlist.id), [collection?.items]);

  // Optimistically write the new membership/order into the cache, then persist the full list.
  const applyOrder = useCallback((orderedIds: string[]) => {
    queryClient.setQueryData(collectionQueryKey(slug!), (old: PlaylistCollectionDetailDto | undefined) => {
      if (!old) return old;
      const byId = new Map(old.items.map((i) => [i.playlist.id, i]));
      const items = orderedIds
        .map((pid, idx) => {
          const item = byId.get(pid);
          return item ? { ...item, position: idx } : null;
        })
        .filter((i): i is NonNullable<typeof i> => i !== null);
      return { ...old, items, playlistCount: items.length };
    });
    setPlaylistsMutation.mutate(orderedIds);
  }, [slug, queryClient, setPlaylistsMutation]);

  const handleReorder = useCallback((orderedIds: string[]) => applyOrder(orderedIds), [applyOrder]);
  const handleRemove = useCallback((playlistId: string) => applyOrder(currentIds.filter((pid) => pid !== playlistId)), [applyOrder, currentIds]);
  const handlePickerConfirm = useCallback((playlistIds: string[]) => applyOrder(playlistIds), [applyOrder]);

  if (isLoading) return <Flex justify="center" p={8}><Spinner size="xl" /></Flex>;
  if (!collection) return <Text>{t('admin.common.noResults', 'Not found')}</Text>;

  return (
    <VStack spacing={5} align="stretch" pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="collection-detail-page">
      <Button variant="link" alignSelf="flex-start" onClick={() => navigate('/playlist-collections')} data-testid="collection-back">
        ← {t('admin.playlistCollections.title', 'Playlist collections')}
      </Button>

      {/* ── Details card ── */}
      <Card borderRadius="20px" boxShadow={cardShadow}>
        <CardHeader pb={0}>
          <Flex justify="space-between" align="center">
            <Heading as="h1" size="md" color={textColor}>{collection.title}</Heading>
            <Flex align="center" gap={3}>
              <Badge colorScheme={collection.isPublished ? 'green' : 'gray'} data-testid="collection-detail-status">
                {collection.isPublished ? t('admin.playlists.published', 'Published') : t('admin.playlists.draft', 'Draft')}
              </Badge>
              <Flex align="center" gap={2}>
                <Text fontSize="sm" color="gray.500">{t('admin.playlistCollections.published', 'Published')}</Text>
                <Switch
                  isChecked={collection.isPublished}
                  onChange={() => publishMutation.mutate(!collection.isPublished)}
                  isDisabled={publishMutation.isPending}
                  colorScheme="green"
                  data-testid="collection-detail-publish-toggle"
                />
              </Flex>
            </Flex>
          </Flex>
        </CardHeader>
        <CardBody>
          <Flex gap={4} direction={{ base: 'column', md: 'row' }} align="flex-end">
            <FormControl>
              <FormLabel>{t('admin.playlistCollections.form.title', 'Default title')}</FormLabel>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="collection-detail-title" />
            </FormControl>
            {TITLE_LOCALES.map(({ code, labelKey }) => (
              <FormControl key={code}>
                <FormLabel>{t(labelKey)}</FormLabel>
                <Input
                  value={titles[code] ?? ''}
                  onChange={(e) => setTitles((prev) => ({ ...prev, [code]: e.target.value }))}
                  data-testid={`collection-detail-title-${code}`}
                />
              </FormControl>
            ))}
            <Button
              colorScheme="brand"
              onClick={() => saveDetailsMutation.mutate()}
              isLoading={saveDetailsMutation.isPending}
              isDisabled={!title.trim()}
              data-testid="collection-detail-save"
            >
              {t('common.save', 'Save')}
            </Button>
          </Flex>
          <Text fontSize="xs" color="gray.400" mt={2}>{t('admin.playlistCollections.form.slug', 'Slug')}: {collection.slug}</Text>
        </CardBody>
      </Card>

      {/* ── Playlists card ── */}
      <Card borderRadius="20px" boxShadow={cardShadow}>
        <CardHeader pb={0}>
          <Flex justify="space-between" align="center">
            <Flex align="center" gap={2}>
              <Heading as="h2" size="md">{t('admin.playlistCollections.playlists', 'Playlists')}</Heading>
              <Badge colorScheme="gray" borderRadius="full">{rows.length}</Badge>
            </Flex>
            <Button size="sm" colorScheme="blue" onClick={() => setPickerOpen(true)} data-testid="collection-add-playlists">
              {t('admin.playlistCollections.addPlaylists', 'Add playlists')}
            </Button>
          </Flex>
        </CardHeader>
        <CardBody>
          <SortablePlaylistList
            rows={rows}
            onReorder={handleReorder}
            onRemove={handleRemove}
            emptyLabel={t('admin.playlistCollections.noPlaylists', 'No playlists in this collection yet')}
            testIdPrefix="collection-playlist"
          />
        </CardBody>
      </Card>

      {pickerOpen && (
        <PlaylistPickerModal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          selectedIds={currentIds}
          onConfirm={handlePickerConfirm}
        />
      )}
    </VStack>
  );
}
